import { useContext } from 'react'
import { Helmet } from 'react-helmet-async'

import { useNumericParams, useWatchEntityUpdates } from '@domain-group/react-hooks'

import {
  Box, Button, Columns, DashboardModule, LoadingModule, PageHeader,
} from '@domain-group/suit-theme'

import quoteAndFinanceTabBarItems from '@sections/Client/packs/Campaign/utils/generateQuoteAndFinanceTabBarItems'

import useCampaign from '@hooks/useCampaign'
import useOrderReconciliation from '@hooks/useOrderReconciliation'

import Activity from '@components/EntityActivity/Activity'
import defaultRequestOptions from '@components/EntityActivity/defaultRequestOptions'
import NotificationBannerLocation from '@components/NotificationBannerLocation'
import NotificationModuleLocation from '@components/NotificationModuleLocation'
import OrderReconciliationList from '@components/OrderReconciliationList'
import ReconciliationTotals from '@components/ReconciliationTotals'

import PageContext from '@contexts/pageContext'

// Activity keys shown in the reconciliation activity log
const RECONCILIATION_ACTIVITY_KEY_PREFIXES = 'order.reconciled,campaign_item.reconciled,campaign_item.updated'

const PageContent = () => {
  const { campaignId } = useNumericParams()

  const { campaign, campaign: { address }, loading: loadingCampaign } = useCampaign({ id: campaignId })

  const pageContext = useContext(PageContext)
  const {
    callbacks: {
      showAddAccountingItemModal,
    },
  } = pageContext

  // Reload the activity log whenever a reconciliation item changes
  const { updatedEntities } = useWatchEntityUpdates(['campaignItems', 'orders'])
  const activityRefetchKey = [updatedEntities.campaignItems, updatedEntities.orders].join('-')

  const orderReconciliationPayload = useOrderReconciliation(
    { campaign_id: campaignId, orderStatusGroupKey: 'complete' },
  )
  const { loadingOrderReconciliationItems } = orderReconciliationPayload

  const loading = loadingOrderReconciliationItems || loadingCampaign

  return (
    <>
      <Helmet>
        <title>{address || 'Campaign'} | Order Reconciliation</title>
      </Helmet>

      <PageHeader
        actionContent={(
          <Button buttonStyle="primaryCreate" onClick={() => showAddAccountingItemModal({ campaign })} size="medium">
            Create Item
          </Button>
        )}
        activeTabBarItemKey="order-reconciliation"
        boxProps={{ height: [112, 105], justifyContent: 'flex-start' }}
        nestedNavigation
        tabBarItems={quoteAndFinanceTabBarItems(campaignId)}
        title="Accounting"
      />

      <Box paddingX="large" paddingTop={[112, 105]}>
        <Columns boxProps={{ marginTop: 'large' }}>
          <Columns.Main>
            <NotificationBannerLocation locationKey="agency.campaign.accounting.banner" />

            <LoadingModule loading={loading} times={3} />

            {!loadingCampaign && !loadingOrderReconciliationItems && (
              // @ts-expect-error TS2741
              <OrderReconciliationList
                campaign={campaign}
                orderReconciliationPayload={orderReconciliationPayload}
                title="Order Reconciliation"
              />
            )}

            {!!campaignId && (
              <DashboardModule title="Reconciliation Activity">
                <Activity
                  boxProps={{
                    marginTop: 0, paddingBottom: 'large', paddingTop: 'medium', paddingLeft: 'large',
                  }}
                  filters={{ campaign_records: true, key_prefixes: RECONCILIATION_ACTIVITY_KEY_PREFIXES, limit: 50 }}
                  refetchKey={activityRefetchKey}
                  requestOptions={defaultRequestOptions.activity}
                  subjectId={campaignId}
                  subjectType="Campaign"
                />
              </DashboardModule>
            )}
          </Columns.Main>

          <Columns.Sidebar>
            <NotificationModuleLocation locationKey="agency.campaign.accounting.sidebar" />

            <ReconciliationTotals campaign={campaign} />
          </Columns.Sidebar>
        </Columns>
      </Box>
    </>
  )
}
export default PageContent
